import { CloseIcon, ReloadIcon } from "assets/icons";
import { SelectedTags } from "components/Common/Table/TableTagInputMobile";
import { produce } from "immer";
import React, { PropsWithChildren } from "react";
import styled from "styled-components";

type SearchParams = {
  [key: string | number | symbol]: unknown | SearchParams;
};

export type ParamStateAction<T extends SearchParams> =
  | T
  | ((prevState: T) => T);
export type ParamStateSetter<T extends SearchParams> = (
  action: ParamStateAction<T>,
) => void;

interface MobileSelectedFilterProps {
  tags: SelectedTags;
  setSearchOptions: ParamStateSetter<{
    searchType: string;
    search: string;
    startDate: string | undefined;
    endDate: string | undefined;
    tags: SelectedTags | undefined;
    page: number;
  }>;
}

const MobileSelectedFilter = ({
  tags,
  setSearchOptions,
}: MobileSelectedFilterProps) => {
  const { region, ...rest } = tags;

  const handleReset = () => {
    setSearchOptions((pre) => ({
      ...pre,
      tags: undefined,
    }));
  };

  return (
    <MobileSelectedFilterLayout>
      <div>
        {tags.region?.region && (
          <MobileSelectedItem
            onDeleteClick={() => {
              setSearchOptions((pre) => ({
                ...pre,
                tags: {
                  ...pre.tags,
                  region: undefined,
                } as SelectedTags,
              }));
            }}
          >
            {tags.region.region}
          </MobileSelectedItem>
        )}
        {tags.region?.city && (
          <MobileSelectedItem
            onDeleteClick={() => {
              setSearchOptions((pre) => ({
                ...pre,
                tags: {
                  ...pre.tags,
                  region: {
                    ...pre.tags?.region,
                    city: "",
                  },
                } as SelectedTags,
              }));
            }}
          >
            {tags.region.city}
          </MobileSelectedItem>
        )}
        {Object.entries(rest).map(([key, value]) => {
          return value.map((tag) => (
            <MobileSelectedItem
              key={key + tag.value}
              onDeleteClick={() => {
                setSearchOptions(
                  produce((draft) => {
                    draft.tags = draft.tags || {};

                    draft.tags[key] = (draft.tags[key] || []).filter(
                      (t) => t.value !== tag.value,
                    );

                    const isTagsEmpty = Object.keys(draft.tags).every(
                      (k) => draft.tags?.[k]?.length === 0,
                    );

                    // tags가 빈 객체라면 undefined로 설정
                    if (isTagsEmpty) {
                      draft.tags = undefined;
                    }
                  }),
                );
              }}
            >
              {tag.title}
            </MobileSelectedItem>
          ));
        })}
      </div>
      {Object.keys(tags).length > 0 && (
        <button className="reload-button" onClick={handleReset}>
          <ReloadIcon />
        </button>
      )}
    </MobileSelectedFilterLayout>
  );
};

export default MobileSelectedFilter;

const MobileSelectedFilterLayout = styled.div`
  width: 100%;
  padding: 1rem 4.8rem 1rem 1rem;
  display: flex;
  align-items: center;

  > div {
    width: 100%;
    display: flex;
    gap: 1.4rem;
    overflow-x: auto;
  }

  ${({ theme }) => theme.typography.caption1.regular};
  color: ${({ theme }) => theme.color.text.secondary};

  .reload-button {
    right: 16px;
    position: absolute;
  }
`;

interface MobileSelectedItemProps extends PropsWithChildren {
  onDeleteClick?: () => void;
}

const MobileSelectedItem = ({
  children,
  onDeleteClick,
}: MobileSelectedItemProps) => {
  return (
    <div
      style={{
        display: "flex",
        gap: "0.4rem",
        alignItems: "center",
        flexShrink: 0,
      }}
    >
      {children}
      <CloseIcon width={16} onClick={onDeleteClick} />
    </div>
  );
};
